/* eslint-disable react-hooks/exhaustive-deps */
import { Company } from '@orientaction/services';
import { getFromLS } from '@orientaction/utils';
import { useEffect, useMemo, useState } from 'react';
import { apiServiceNative } from '../../../utils/fetchNative';
import { ASSIGNABLE_STAFF_ROLE_IDS } from '../shared/constants';
import { toId, unwrapCollection } from '../shared/utils';
import {
  AssignmentResourceFilter,
  buildAssignmentResourceFilter,
  collectDescendantIds,
  collectHierarchyUserIds,
  needsTeamOwnerIds,
  shouldSkipAssignmentFilter,
} from './assignmentVisibility';

const qs = require('qs');

const readConnectedUser = () => {
  try {
    return JSON.parse(getFromLS('userInfos') || '{}');
  } catch {
    return {};
  }
};

const unwrapPopulatePayload = (response: any) =>
  response?.data?.data ?? response?.data ?? response;

const fetchTeamOwnerIds = async (viewer: any, token: string): Promise<number[] | null> => {
  const viewerId = toId(viewer?.id);
  if (!viewerId) {
    return null;
  }

  try {
    const populateRes = await Company.getPopulateHierachy(token, viewerId);
    const fromPopulate = collectHierarchyUserIds(unwrapPopulatePayload(populateRes));
    if (fromPopulate.length > 0) {
      return Array.from(new Set([viewerId, ...fromPopulate]));
    }
  } catch {
    // Fallback below: walk the company tree from upper_hierarchy.
  }

  const companyId = toId(viewer?.company?.id ?? viewer?.company?.data?.id ?? viewer?.company);
  if (!companyId) {
    return [viewerId];
  }

  try {
    const params = qs.stringify(
      {
        filters: {
          company: { id: { $eq: companyId } },
          company_user_role: { id: { $in: ASSIGNABLE_STAFF_ROLE_IDS } },
          isActivated: { $eq: true },
        },
        fields: ['id'],
        populate: {
          upper_hierarchy: { fields: ['id'] },
        },
        pagination: { pageSize: 200 },
      },
      { encodeValuesOnly: true }
    );
    const staff = unwrapCollection(await apiServiceNative.get(`/api/users?${params}`));
    const descendants = collectDescendantIds(viewerId, staff);
    return descendants.length > 0 ? descendants : [viewerId];
  } catch {
    return null;
  }
};

export const useAssignmentResourceFilter = (beneficiary: any) => {
  const token = getFromLS('userToken') || '';
  const viewer = useMemo(() => readConnectedUser(), []);
  const skipFilter = shouldSkipAssignmentFilter(viewer);
  const loadTeam = needsTeamOwnerIds(viewer);
  const [teamOwnerIds, setTeamOwnerIds] = useState<number[] | null>(
    skipFilter || !loadTeam ? (toId(viewer?.id) ? [Number(toId(viewer.id))] : []) : null
  );
  const [ready, setReady] = useState(skipFilter || !loadTeam);

  useEffect(() => {
    if (skipFilter || !loadTeam || !token) {
      return undefined;
    }
    let cancelled = false;

    const load = async () => {
      const ids = await fetchTeamOwnerIds(viewer, token);
      if (!cancelled) {
        setTeamOwnerIds(ids);
        setReady(true);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [skipFilter, loadTeam, token, viewer?.id]);

  const filter: AssignmentResourceFilter | null = useMemo(() => {
    if (skipFilter || !beneficiary?.id || !teamOwnerIds) {
      return null;
    }
    return buildAssignmentResourceFilter(beneficiary, teamOwnerIds);
  }, [skipFilter, beneficiary, teamOwnerIds]);

  return { ready, filter };
};
